/**
 * 営業パイプライン管理用の API hooks
 */
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";

/**
 * パイプラインアイテムの型
 */
export type SalesPipelineItem = {
  id: string;
  talentId: string;
  talentName: string | null;
  focusMatterId: string | null;
  focusMatterTitle: string | null;
  opportunitySource: string | null;
  stage: string;
  createdByType: string | null;
  clientCompany: string | null;
  matterTitle: string | null;
  proposedReward: number | null;
  notes: string | null;
  lostReason: string | null;
  assignedTo: string | null;
  assigneeName: string | null;
  nextActionDate: string | null;
  nextActionMemo: string | null;
  stageChangedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * パイプライン一覧レスポンスの型
 */
type SalesPipelineListResponse = {
  items: SalesPipelineItem[];
  total: number;
  page: number;
  limit: number;
};

/**
 * パイプラインステージサマリー項目の型
 */
export type PipelineStageSummaryItem = {
  stage: string;
  count: number;
};

/**
 * パイプラインステージサマリーレスポンスの型
 */
type PipelineStageSummaryResponse = {
  items: PipelineStageSummaryItem[];
  total: number;
};

/**
 * パイプラインフィルターの型
 */
export type PipelineFilters = {
  stage?: string;
  talentId?: string;
  assignedTo?: string;
  createdByType?: string;
  focusMatterId?: string;
  page?: number;
  limit?: number;
};

/**
 * フィルターをクエリパラメータに変換
 * @param {object} filters フィルター
 * @return {Record<string, string>} クエリパラメータ
 */
function filtersToParams(filters: PipelineFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.stage) params.stage = filters.stage;
  if (filters.talentId) params.talentId = filters.talentId;
  if (filters.assignedTo) params.assignedTo = filters.assignedTo;
  if (filters.createdByType) params.createdByType = filters.createdByType;
  if (filters.focusMatterId) params.focusMatterId = filters.focusMatterId;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  return params;
}

/**
 * パイプライン一覧を取得する hook
 * @param {object} filters フィルター条件
 * @param {string} [filters.stage] ステージ
 * @param {string} [filters.talentId] タレントID
 * @param {string} [filters.assignedTo] 担当者
 * @param {string} [filters.createdByType] 作成元種別
 * @param {string} [filters.focusMatterId] 注目案件ID
 * @param {number} [filters.page] ページ番号
 * @param {number} [filters.limit] 取得件数
 * @return {object} pipelines, total, loading, error, refetch
 */
export function useSalesPipelines(filters: PipelineFilters = {}): {
  pipelines: SalesPipelineItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [pipelines, setPipelines] = useState<SalesPipelineItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const filterKey = JSON.stringify(filters);

  const fetchPipelines = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = filtersToParams(JSON.parse(filterKey) as PipelineFilters);
      const response = await restClient.get<SalesPipelineListResponse>(
        "/sales-pipelines",
        params,
      );
      setPipelines(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  return { pipelines, total, loading, error, refetch: fetchPipelines };
}

/**
 * パイプラインステージサマリーを取得する hook
 * @return {object} summary, total, loading, error, refetch
 */
export function usePipelineSummary(): {
  summary: PipelineStageSummaryItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [summary, setSummary] = useState<PipelineStageSummaryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<PipelineStageSummaryResponse>(
        "/sales-pipelines/summary",
      );
      setSummary(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, total, loading, error, refetch: fetchSummary };
}

/**
 * パイプラインを作成する hook
 * @return {object} createPipeline, loading, error
 */
export function useCreatePipeline(): {
  createPipeline: (
    data: Partial<SalesPipelineItem>,
  ) => Promise<SalesPipelineItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createPipeline = useCallback(
    async (data: Partial<SalesPipelineItem>): Promise<SalesPipelineItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<SalesPipelineItem>(
          "/sales-pipelines",
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createPipeline, loading, error };
}

/**
 * パイプラインを更新する hook
 * @return {object} updatePipeline, loading, error
 */
export function useUpdatePipeline(): {
  updatePipeline: (
    id: string,
    data: Partial<SalesPipelineItem>,
  ) => Promise<SalesPipelineItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updatePipeline = useCallback(
    async (
      id: string,
      data: Partial<SalesPipelineItem>,
    ): Promise<SalesPipelineItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.patch<SalesPipelineItem>(
          `/sales-pipelines/${id}`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updatePipeline, loading, error };
}

/**
 * パイプラインのステージを更新する hook
 * @return {object} updateStage, loading, error
 */
export function useUpdatePipelineStage(): {
  updateStage: (
    id: string,
    data: { stage: string; lostReason?: string },
  ) => Promise<SalesPipelineItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateStage = useCallback(
    async (
      id: string,
      data: { stage: string; lostReason?: string },
    ): Promise<SalesPipelineItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.patch<SalesPipelineItem>(
          `/sales-pipelines/${id}/stage`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateStage, loading, error };
}

/**
 * パイプラインを削除する hook
 * @return {object} deletePipeline, loading, error
 */
export function useDeletePipeline(): {
  deletePipeline: (id: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deletePipeline = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await restClient.delete(`/sales-pipelines/${id}`);
    } catch (e) {
      const apiError =
        e instanceof RestApiError
          ? e
          : new RestApiError(
              e instanceof Error ? e.message : "Unknown error",
              500,
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deletePipeline, loading, error };
}
